/**
 * @file Redux mappers for composeMetaLabels container
 */

import { MapDispatchToProps, MapStateToProps } from 'react-redux';

import AppState from '../../../models/state';
import {
  addDraftLabelRequest,
  removeDraftLabelRequest,
  saveDraftLabelRequest,
  searchDraftLabelsRequest,
} from '../../../store/actions/draftsLabels';

import { DispatchProps, OwnProps, StateProps } from './types';

/**
 * Map Redux app state to component props
 *
 * @param appState          Current app state
 * @param appState.debug    Debug state of the app
 * @param appState.drafts   Drafts in state
 * @param ownProps          Passed props
 * @param ownProps.nodeType The id of the draft
 * @returns                 Mapped state
 */
export const mapStateToProps: MapStateToProps<
  StateProps,
  OwnProps,
  AppState
> = ({ debug, drafts }, { nodeType }) => {
  const draft = drafts.get(nodeType);

  if (draft === undefined) {
    return {
      dataSearchValue: '',
      dataSelected: new Set(),
      debugLoading: debug.networkCompose,
    };
  }

  return {
    dataSearchValue: draft.labelsSearch,
    dataSelected: draft.labelsCurrent,
    debugLoading: debug.networkCompose,
  };
};

/**
 * Map Redux action dispatch to component props
 *
 * @param dispatch          Redux action dispatcher
 * @param ownProps          Passed props
 * @param ownProps.nodeType The id of the draft
 * @returns                 Mapped state
 */
export const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (
  dispatch,
  { nodeType },
) => ({
  /**
   * Dispatches request to add label draft
   *
   * @param labelId Id of the label to add
   *
   * @returns       void
   */
  requestLabelAdd: labelId => dispatch(addDraftLabelRequest(nodeType, labelId)),
  /**
   * Dispatches request to remove label draft
   *
   * @param labelId Id of the label to remove
   */
  requestLabelRemove: labelId => {
    dispatch(removeDraftLabelRequest(nodeType, labelId));
  },
  /**
   * Dispatches request to save label draft
   *
   * @returns void
   */
  requestLabelsSave: () => dispatch(saveDraftLabelRequest(nodeType)),
  /**
   * Dispatches request for searching labels draft
   *
   * @param value String to search for
   */
  requestLabelsSearch: value => {
    dispatch(searchDraftLabelsRequest(nodeType, value));
  },
});
